import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch, useAppSelector } from '../../../redux';
import { loadUsers } from '../../../redux';
import { Link, useNavigate } from "react-router-dom";
import { Ability, Button, Icon, UserAvatar } from '../../../components';
import { Badge, Table } from 'flowbite-react';
import { User } from '../../../models';

function UsersList() {
    const { t }    = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const searchInput         = useRef<HTMLInputElement>(null);
    const [search, setSearch] = useState("");
    const [showDisabled, setShowDisabled] = useState(false);

    const { users } = useAppSelector((state: RootState) => state.users);

    useEffect(() => {
        dispatch(loadUsers({ include_disabled: true }));
    }, [dispatch]);

    const hasDisabledUsers = useMemo(
        () => (users || []).some((user: User) => user.disabled === true),
        [users]
    );

    useEffect(() => {
        if (!hasDisabledUsers && showDisabled)
            setShowDisabled(false);
    }, [hasDisabledUsers, showDisabled]);

    const filteredUsers = useMemo(() => {
        const byStatus = (users || []).filter((user: User) =>
            showDisabled ? user.disabled === true : user.disabled !== true
        );

        if (search?.length < 3)
            return byStatus;

        return byStatus.filter((user: User) => {
            return user.fullname().match(new RegExp(search, 'gi'));
        });
    }, [users, search, showDisabled]);

    const nonBotUsers = useMemo(
        () => filteredUsers?.filter((user: User) => user.account_type !== User.ACCOUNT_TYPE_BOT) || [],
        [filteredUsers]
    );
    const botUsers = useMemo(
        () => filteredUsers?.filter((user: User) => user.account_type === User.ACCOUNT_TYPE_BOT) || [],
        [filteredUsers]
    );

    const usersByRole = {
        "adminUsers"    : nonBotUsers.filter((user: User) => user?.role === User.ROLE_ADMIN),
        "simpleUsers"   : nonBotUsers.filter((user: User) => user?.role === User.ROLE_USER),
        "externalUsers" : nonBotUsers.filter((user: User) => user?.role === User.ROLE_EXTERNAL),
        "clientUsers"   : nonBotUsers.filter((user: User) => user?.role === User.ROLE_CLIENT)
    }

    if (searchInput?.current)
        searchInput.current.focus();

    const renderUserRow = (user: User) => (
        <Table.Row key={user?._id} className="bg-white dark:border-gray-700 dark:bg-gray-800">
            <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                <div className="flex flex-col gap-2 md:flex-row md:justify-between md:items-center">
                    <Link to={`/users/edit/${user._id}`} className="flex gap-x-3">
                        <UserAvatar user={user} hideInitials size={10} />
                        <div className="flex-row">
                            <span className="inline-flex items-center gap-2">
                                {user?.fullname()}
                                {user.disabled === true && (
                                    <Badge color="gray">{t('users.disabled')}</Badge>
                                )}
                            </span>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{user.email}</div>
                        </div>
                    </Link>
                    <Button title={t('users.edit_this_user')} icon="edit" iconSize={4} color="primary" onClick={() => navigate(`/users/edit/${user._id}`)} small />
                </div>
            </Table.Cell>
        </Table.Row>
    );

    return (
        <Ability can="user:read" redirect="/">
            <div className="UsersList">
                <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 py-0 sm:px-6 lg:px-8">
                    <div className="my-2 mx-auto lg:flex lg:items-center lg:justify-between">
                        <div>
                            <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                                <Icon type="user" size={7} className="mr-1.5 inline" />
                                { t('app.users') }
                            </h2>
                        </div>
                        <div className="mt-5 flex flex-col space-y-2 sm:space-y-0 sm:flex-row lg:ml-4 lg:mt-0">
                            {hasDisabledUsers && (
                                <span className="sm:mr-3">
                                    <Button
                                        className="w-full"
                                        title={t('users.show_disabled')}
                                        color={showDisabled ? 'secondary' : 'default'}
                                        icon="archive"
                                        onClick={() => setShowDisabled(!showDisabled)}
                                    />
                                </span>
                            )}
                            <div className="relative flex">
                                <input
                                    type="text"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    ref={searchInput}
                                    className="w-full h-9 text-gray-600 dark:text-gray-300 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 rounded-md focus:ring-gray-300 dark:ring-gray-600 border-0 focus:border-0 py-0 pr-8"
                                />
                                <Icon type="search" color="gray-600" className="absolute top-2.5 right-2.5" />
                            </div>
                            <span className="sm:ml-3">
                                <Button title={t('users.add_a_user')} color="navigate" to="/users/new" icon="plus" />
                            </span>
                        </div>
                    </div>
                </header>
                <main>
                    <div className="grid grid-cols-1 gap-4">
                        {Object.keys(usersByRole).filter((key: any) => usersByRole[key as keyof typeof usersByRole]?.length).map((key) => (
                        <div className="p-5" key={key}>
                            <Table striped hoverable>
                                <Table.Head>
                                    <Table.HeadCell colSpan={2}>{t("users."+key)}</Table.HeadCell>
                                </Table.Head>
                                <Table.Body className="divide-y">
                                    {(usersByRole[key as keyof typeof usersByRole]).sort((a: User, b: User) => a.firstname > b.firstname).map(renderUserRow)}
                                </Table.Body>
                            </Table>
                        </div>
                        ))}
                        {botUsers.length > 0 && (
                        <div className="p-5" key="botUsers">
                            <Table striped hoverable>
                                <Table.Head>
                                    <Table.HeadCell colSpan={2}>{t("users.botUsers")}</Table.HeadCell>
                                </Table.Head>
                                <Table.Body className="divide-y">
                                    {botUsers.sort((a: User, b: User) => a.firstname > b.firstname).map(renderUserRow)}
                                </Table.Body>
                            </Table>
                        </div>
                        )}
                    </div>
                </main>
            </div>
        </Ability>
    );
}

export default UsersList;
